<template>
  <div class="detail-container">
    <!-- 顶部导航栏（毛玻璃+金属边） -->
    <div class="nav-bar">
      <div class="nav-left" @click="goback">
        <van-icon name="arrow-left" size="20" class="back-icon" />
        <span>返回</span>
      </div>
      <div class="nav-title">委托详情</div>
      <div class="nav-right"></div>
    </div>

    <!-- 主内容滚动区域 -->
    <div class="content-scroll">
      <!-- 企业名称卡片 -->
      <div class="company-card">
        <div class="company-name">{{ orderObject.CompanyName || '--' }}</div>
        <div class="company-id" style="margin-bottom: 8px;">委托单号：{{ orderId || '--' }}</div>
        <div class="company-id">客户试样编号：{{ orderObject['CustomerSampleNo'] || '--' }}</div>
      </div>

      <!-- 步骤条 -->
      <div class="steps-wrapper">
        <van-steps :active="orderObject.Stage || 0" active-color="#2c7be5" inactive-color="#c0ccda">
          <van-step>收样完毕</van-step>
          <van-step>加工完毕</van-step>
          <van-step>试验完毕</van-step>
          <van-step>报告出具</van-step>
        </van-steps>
      </div>

      <!-- 信息面板 -->
      <div class="info-grid">
        <div class="info-item">
          <span class="info-label">合同拟定日期</span>
          <span class="info-value">{{ orderObject.ContractTime || '--' }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">预计完成日期</span>
          <span class="info-value">{{ orderObject.PlanTime || '--' }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">送样日期</span>
          <span class="info-value">{{ orderObject.SendSampleTime || '--' }}</span>
        </div>
      </div>

      <!-- 客户委托单区域 -->
      <div class="report-section" v-if="orderObject.CcOrderPdfUploadFilePath != '' && orderObject.CcOrderPdfUploadFilePath != undefined">
        <div class="section-title">客户委托单</div>
        <div class="report-list">
          <div class="report-item">
            <div class="report-info">
              <van-icon name="description" size="20" class="report-icon" />
              <span class="report-name">客户委托单(PDF)</span>
            </div>
            <div class="report-action" @click="viewPdf(orderObject.CcOrderPdfUploadFilePath)">
              <span class="view-btn">查看</span>
              <van-icon name="arrow" size="14" />
            </div>
          </div>
        </div>
      </div>

      <!-- ========== 检测费用明细（可折叠） ========== -->
      <div
        class="fee-section"
        v-if="(orderObject.ChargeSheets && orderObject.ChargeSheets.length) || orderObject.TotalFee || orderObject.ExpressFee"
      >
        <div class="fee-card">
          <!-- 头部：始终可见，展示标题 + 总费用 + 展开箭头 -->
          <div class="fee-header" @click="toggleFee">
            <div class="fee-header-left">
              <span class="fee-header-title">检测费用明细</span>
              <span class="fee-header-count" v-if="orderObject.ChargeSheets && orderObject.ChargeSheets.length">
                共 {{ orderObject.ChargeSheets.length }} 项
              </span>
            </div>
            <div class="fee-header-right">
              <span class="total-price">¥{{ formatMoney(totalFee) }}</span>
              <van-icon
                :name="feeExpanded ? 'arrow-up' : 'arrow-down'"
                size="14"
                class="fee-arrow"
                :class="{ 'fee-arrow--open': feeExpanded }"
              />
            </div>
          </div>

          <!-- 明细列表：展开后显示 -->
          <div class="fee-list" v-show="feeExpanded">
            <div v-for="(item, idx) in orderObject.ChargeSheets" :key="'fee' + idx" class="fee-item">
              <div class="fee-info">
                <div class="fee-name">{{ item.ItemName || '--' }}</div>
                <div class="fee-sub">
                  <span v-if="item.TestStandard">{{ item.TestStandard }}</span>
                  <span v-if="item.TestStandard && item.Quantity"> · </span>
                  <span v-if="item.Quantity">{{ item.Quantity }} 件</span>
                </div>
              </div>
              <div class="fee-price">¥{{ formatMoney(item.ItemCharge) }}</div>
            </div>

            <!-- 快递费用（存在且不为 0 时展示） -->
            <div v-if="Number(orderObject.ExpressFee) > 0" class="fee-item">
              <div class="fee-info">
                <div class="fee-name">快递费用</div>
              </div>
              <div class="fee-price">¥{{ formatMoney(orderObject.ExpressFee) }}</div>
            </div>

            <!-- 明细底部再次展示总费用，方便对照 -->
            <div class="fee-item fee-total">
              <div class="fee-info">
                <div class="fee-name">总费用</div>
              </div>
              <div class="fee-price total-price">¥{{ formatMoney(totalFee) }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ========== 加工进度区域 ========== -->
      <div v-if="orderObject.ProcessProcesses && orderObject.ProcessProcesses.length" class="process-list">
        <div class="section-title">加工进度</div>
        <div v-for="(item, idx) in orderObject.ProcessProcesses" :key="'process' + idx" class="process-card">
          <div class="process-header">
            <span class="process-title">{{ item.ItemName }}</span>
            <span class="process-time">{{ item.CompeleteNum }}/{{ item.AllNum }}</span>
          </div>
          <van-progress
            :percentage="getPercentage(item.CompeleteNum, item.AllNum)"
            color="#2c7be5"
            stroke-width="12"
            track-color="#e9ecef"
          />
        </div>
      </div>

      <!-- ========== 试验进度区域 ========== -->
      <div v-if="orderObject.TestProcesses && orderObject.TestProcesses.length" class="process-list">
        <div class="section-title">试验进度</div>
        <div v-for="(item, idx) in orderObject.TestProcesses" :key="'test' + idx" class="process-card">
          <div class="process-header">
            <span class="process-title">{{ item.ItemName }}</span>
            <span class="process-time">{{ item.CompeleteNum }}/{{ item.AllNum }}</span>
          </div>
          <van-progress
            :percentage="getPercentage(item.CompeleteNum, item.AllNum)"
            color="#2c7be5"
            stroke-width="12"
            track-color="#e9ecef"
          />
        </div>
      </div>

      <!-- 各流程进度卡片 -->
      <div class="process-list" v-if="orderObject.Processes && orderObject.Processes.length">
        <div v-for="(item, i) in orderObject.Processes" :key="i" class="process-card">
          <div class="process-header">
            <span class="process-title">{{ item.Title }}</span>
            <span class="process-time">{{ item.CreateTime }}</span>
          </div>
          <van-progress :percentage="item.Percent || 0" color="#2c7be5" stroke-width="12" track-color="#e9ecef" />
        </div>
      </div>

      <!-- 照片入口 -->
      <div
        class="photo-section"
        v-if="
          (orderObject.SpecimenPhotoPathList1 && orderObject.SpecimenPhotoPathList1.length) ||
            (orderObject.SpecimenPhotoPathList2 && orderObject.SpecimenPhotoPathList2.length) ||
            (orderObject.SpecimenPhotoPathList3 && orderObject.SpecimenPhotoPathList3.length) ||
            (orderObject.SpecimenPhotoPathList4 && orderObject.SpecimenPhotoPathList4.length)
        "
      >
        <div class="section-title">相关资料</div>
        <div class="photo-buttons">
          <div v-if="orderObject.SpecimenPhotoPathList1 && orderObject.SpecimenPhotoPathList1.length" class="photo-btn" @click="viewPic(1)">
            <van-icon name="photograph" size="20" />
            <span>收样照片</span>
            <span class="photo-count">{{ orderObject.SpecimenPhotoPathList1.length }}</span>
          </div>
          <div v-if="orderObject.SpecimenPhotoPathList2 && orderObject.SpecimenPhotoPathList2.length" class="photo-btn" @click="viewPic(2)">
            <van-icon name="photograph" size="20" />
            <span>加工照片</span>
            <span class="photo-count">{{ orderObject.SpecimenPhotoPathList2.length }}</span>
          </div>
          <div v-if="orderObject.SpecimenPhotoPathList3 && orderObject.SpecimenPhotoPathList3.length" class="photo-btn" @click="viewPic(3)">
            <van-icon name="photograph" size="20" />
            <span>试验照片</span>
            <span class="photo-count">{{ orderObject.SpecimenPhotoPathList3.length }}</span>
          </div>
          <div v-if="orderObject.SpecimenPhotoPathList4 && orderObject.SpecimenPhotoPathList4.length" class="photo-btn" @click="viewPic(4)">
            <van-icon name="photograph" size="20" />
            <span>报告照片</span>
            <span class="photo-count">{{ orderObject.SpecimenPhotoPathList4.length }}</span>
          </div>
        </div>
      </div>

      <!-- 报告列表区域 -->
      <div class="report-section" v-if="orderObject.Stage === 3 && orderObject.ReportPdfList && orderObject.ReportPdfList.length">
        <div class="section-title">报告文件</div>
        <div class="report-list">
          <div v-for="(report, idx) in orderObject.ReportPdfList" :key="idx" class="report-item">
            <div class="report-info">
              <van-icon name="description" size="20" class="report-icon" />
              <span class="report-name">{{ report.ReportNo || '未命名报告' }}</span>
            </div>
            <div class="report-action" @click="viewPdf(report.PdfPath)">
              <span class="view-btn">查看</span>
              <van-icon name="arrow" size="14" />
            </div>
          </div>
        </div>
      </div>

      <!-- 图片预览组件 -->
      <van-image-preview v-model="show" :images="images" @change="onChange">
        <template v-slot:cover>
          <div class="preview-remark">{{ imageRemark }}</div>
        </template>
      </van-image-preview>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { Progress, Toast, Step, Steps, ImagePreview, Icon } from 'vant'
import axios from 'axios'

Vue.use(Step)
Vue.use(Steps)
Vue.use(Progress)
Vue.use(Toast)
Vue.use(ImagePreview)
Vue.use(Icon)

export default {
  data() {
    return {
      orderObject: {},
      orderId: '',
      specimenPhotoArray: [],
      images: [],
      show: false,
      imageRemark: '',
      // 检测费用明细展开状态（默认收起，仅展示总费用）
      feeExpanded: false
    }
  },
  computed: {
    // 总费用：优先使用后端返回的 TotalFee，缺失时按明细汇总
    totalFee() {
      const total = this.orderObject.TotalFee
      if (total !== undefined && total !== null && total !== '') {
        return Number(total) || 0
      }
      let sum = 0
      const list = this.orderObject.ChargeSheets
      if (list && list.length) {
        sum = list.reduce((acc, cur) => acc + (Number(cur.ItemCharge) || 0), 0)
      }
      return sum
    }
  },
  created() {
    this.getGoods()
  },
  methods: {
    goback() {
      if (window.history.length > 1) {
        this.$router.go(-1)
      } else {
        this.$router.push('/index')
      }
    },
    getGoods() {
      this.orderId = this.$route.query.orderId
      const _that = this

      const postLoading = Toast.loading({
        duration: 0,
        forbidClick: true,
        message: '加载中...'
      })

      axios
        .get(window.config.apiUrl + '/api/Public/GetProcessInfo', {
          params: { orderId: _that.orderId }
        })
        .then(function(response) {
          postLoading.clear()
          if (response.data.code == 1) {
            _that.orderObject = response.data.data
          } else {
            Toast(response.data.msg)
          }
        })
        .catch(function(error) {
          postLoading.clear()
          Toast('网络错误')
        })
    },
    // 切换检测费用明细展开/收起
    toggleFee() {
      this.feeExpanded = !this.feeExpanded
    },
    viewReport() {
      if (this.orderObject.ReportFilePath) {
        window.open(this.orderObject.ReportFilePath, '_blank')
      }
    },
    viewPic(index) {
      let tmpSpecimenPhotoArray = []
      const photoMaps = {
        1: 'SpecimenPhoto1',
        2: 'SpecimenPhoto2',
        3: 'SpecimenPhoto3',
        4: 'SpecimenPhoto4'
      }
      const photoList = this.orderObject[photoMaps[index]]
      if (photoList && photoList.length) {
        tmpSpecimenPhotoArray = photoList.map(it => ({
          Path: (window.config.apiUrl2 + '/' + it.Path).replace(/\\/g, '/'),
          Remark: it.Remark
        }))
      }
      this.show = true
      this.specimenPhotoArray = tmpSpecimenPhotoArray
      this.images = this.specimenPhotoArray.map(it => it.Path)
      if (tmpSpecimenPhotoArray.length) {
        this.imageRemark = tmpSpecimenPhotoArray[0].Remark
      }
    },
    onChange(idx) {
      if (this.specimenPhotoArray.length > idx) {
        this.imageRemark = this.specimenPhotoArray[idx].Remark
      }
    },
    // 查看PDF文件（支持完整路径或相对路径拼接）
    viewPdf(pdfPath) {
      if (pdfPath.indexOf('http') == -1 && pdfPath.indexOf('https') == -1) {
        pdfPath = window.config.apiUrl2 + '/' + pdfPath.replace(/\\/g, '/')
      }
      // 保存当前滚动位置
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop

      if (pdfPath) {
        window.open(pdfPath, '_blank')
      }

      // 延迟恢复滚动位置并强制重绘（iOS/Android WebView 兼容）
      setTimeout(() => {
        // 恢复滚动位置
        document.documentElement.scrollTop = scrollTop
        document.body.scrollTop = scrollTop

        // 强制重绘：临时修改并恢复某个元素的样式
        const container = document.querySelector('.content-scroll')
        if (container) {
          container.style.transform = 'translateZ(0)'
          setTimeout(() => {
            container.style.transform = ''
          }, 50)
        }

        // 触发 window 的 resize 事件，强制重新计算布局
        window.dispatchEvent(new Event('resize'))
      }, 100)
    },
    // 计算百分比（处理分母为0及异常情况）
    getPercentage(complete, all) {
      if (!all || all === 0) return 0
      let percent = (complete / all) * 100
      // 限制最大100%，并取整
      return Math.min(Math.floor(percent), 100)
    },
    // 金额格式化：千分位 + 两位小数
    formatMoney(val) {
      const num = Number(val)
      const fixed = (isNaN(num) ? 0 : num).toFixed(2)
      const parts = fixed.split('.')
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      return parts.join('.')
    }
  }
}
</script>

<style scoped>
/* 全局金属质感基色 */
.detail-container {
  --metal-bg: #f4f6fa;
  --metal-surface: rgba(255, 255, 255, 0.92);
  --metal-border: rgba(0, 0, 0, 0.08);
  --metal-glow: 0 8px 20px -8px rgba(0, 0, 0, 0.1);
  --accent: #2c7be5;
  --accent-light: #5d9eff;
  --text-primary: #1e2a3e;
  --text-secondary: #5c6f87;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  background: linear-gradient(145deg, #eef2f6 0%, #d9e0e8 100%);
  min-height: 100vh;
}

/* 顶部导航栏（毛玻璃+金属边） */
.nav-bar {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
}
.nav-left {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  font-weight: 500;
  color: var(--accent);
  cursor: pointer;
  transition: opacity 0.2s;
}
.nav-left:active {
  opacity: 0.6;
}
.back-icon {
  font-size: 20px;
}
.nav-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  letter-spacing: -0.3px;
}
.nav-right {
  width: 50px;
}

/* 滚动区域 */
.content-scroll {
  padding: 16px;
  padding-bottom: 40px;
}

/* 企业名称卡片 */
.company-card {
  background: var(--metal-surface);
  border-radius: 24px;
  padding: 20px 16px;
  margin-bottom: 20px;
  box-shadow: var(--metal-glow);
  border: 1px solid rgba(255, 255, 255, 0.6);
  text-align: center;
}
.company-name {
  font-size: 22px;
  font-weight: 700;
  background: linear-gradient(135deg, #1f2a48, #2c3e66);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  margin-bottom: 8px;
}
.company-id {
  font-size: 13px;
  color: var(--text-secondary);
  letter-spacing: 0.3px;
}

/* 步骤条容器 */
.steps-wrapper {
  background: var(--metal-surface);
  border-radius: 28px;
  padding: 16px 12px;
  margin-bottom: 20px;
  box-shadow: var(--metal-glow);
  border: 1px solid rgba(255, 255, 255, 0.5);
}
/* Vue 2 深度选择器兼容写法 */
.steps-wrapper /deep/ .van-step__title {
  font-weight: 500;
  font-size: 14px;
}
.steps-wrapper /deep/ .van-step--finish .van-step__title {
  color: var(--accent);
}
.steps-wrapper /deep/ .van-step--active .van-step__title {
  font-weight: 600;
}

/* 信息网格 */
.info-grid {
  background: var(--metal-surface);
  border-radius: 24px;
  padding: 16px;
  margin-bottom: 20px;
  box-shadow: var(--metal-glow);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 12px;
  border: 1px solid rgba(255, 255, 255, 0.5);
}
.info-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.info-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
  letter-spacing: 0.5px;
}
.info-value {
  font-size: 15px;
  font-weight: 500;
  color: var(--text-primary);
}
.highlight {
  color: var(--accent);
  font-weight: 600;
}
.report-link {
  margin-top: 6px;
  font-size: 12px;
  color: var(--accent);
  font-weight: 500;
  cursor: pointer;
  display: inline-block;
  width: fit-content;
  border-bottom: 1px dashed var(--accent);
}

/* 流程卡片 */
.process-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}
.process-card {
  background: var(--metal-surface);
  border-radius: 20px;
  padding: 16px;
  box-shadow: var(--metal-glow);
  border: 1px solid rgba(255, 255, 255, 0.5);
  transition: transform 0.2s, box-shadow 0.2s;
}
.process-card:active {
  transform: scale(0.99);
}
.process-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
}
.process-title {
  font-weight: 600;
  font-size: 16px;
  color: var(--text-primary);
}
.process-time {
  font-size: 12px;
  color: var(--text-secondary);
}
.process-card /deep/ .van-progress {
  border-radius: 20px;
  overflow: hidden;
}

/* 照片区域 */
.photo-section {
  margin-top: 8px;
}
.section-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 12px;
  padding-left: 4px;
  border-left: 3px solid var(--accent);
}
.photo-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.photo-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--metal-surface);
  border-radius: 40px;
  padding: 8px 18px;
  box-shadow: var(--metal-glow);
  border: 1px solid rgba(255, 255, 255, 0.6);
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s;
}
.photo-btn:active {
  transform: translateY(1px);
  background: rgba(255, 255, 255, 0.8);
}
.photo-count {
  background: var(--accent);
  color: white;
  border-radius: 20px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  margin-left: 4px;
}

/* 图片预览备注 */
.preview-remark {
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  color: white;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  padding: 8px;
  margin: 0 16px;
  border-radius: 40px;
  pointer-events: none;
}

/* 报告列表区域 */
.report-section {
  margin-bottom: 20px;
}
.report-list {
  background: var(--metal-surface);
  border-radius: 20px;
  padding: 4px 0;
  box-shadow: var(--metal-glow);
  border: 1px solid rgba(255, 255, 255, 0.5);
  overflow: hidden;
}
.report-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  transition: background 0.2s;
}
.report-item:last-child {
  border-bottom: none;
}
.report-item:active {
  background: rgba(0, 0, 0, 0.02);
}
.report-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}
.report-icon {
  color: var(--accent);
  flex-shrink: 0;
}
.report-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}
.report-action {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(44, 123, 229, 0.1);
  padding: 6px 12px;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}
.report-action:active {
  background: rgba(44, 123, 229, 0.2);
  transform: scale(0.96);
}
.view-btn {
  font-size: 13px;
  font-weight: 500;
  color: var(--accent);
}

/* ========== 检测费用明细（可折叠） ========== */
.fee-section {
  margin-bottom: 20px;
}
.fee-card {
  background: var(--metal-surface);
  border-radius: 20px;
  box-shadow: var(--metal-glow);
  border: 1px solid rgba(255, 255, 255, 0.5);
  overflow: hidden;
}

/* 头部：可点击区域 */
.fee-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.2s;
}
.fee-header:active {
  background: rgba(0, 0, 0, 0.02);
}
.fee-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.fee-header-title {
  position: relative;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  padding-left: 10px;
}
.fee-header-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 16px;
  border-radius: 2px;
  background: var(--accent);
}
.fee-header-count {
  font-size: 12px;
  color: var(--text-secondary);
  background: rgba(44, 123, 229, 0.08);
  padding: 2px 8px;
  border-radius: 20px;
  white-space: nowrap;
}
.fee-header-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.fee-arrow {
  color: var(--text-secondary);
  transition: transform 0.25s ease;
}
.fee-arrow--open {
  color: var(--accent);
  transform: rotate(180deg);
}

/* 总费用（红色大字） */
.total-price {
  font-size: 20px;
  font-weight: 700;
  color: #f2453d;
  letter-spacing: -0.3px;
  white-space: nowrap;
}

/* 明细列表 */
.fee-list {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  animation: feeFadeIn 0.22s ease;
}
@keyframes feeFadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.fee-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.fee-item:last-child {
  border-bottom: none;
}
.fee-info {
  flex: 1;
  min-width: 0;
  padding-right: 12px;
}
.fee-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.4;
  word-break: break-all;
}
.fee-sub {
  margin-top: 4px;
  font-size: 12px;
  color: var(--text-secondary);
  letter-spacing: 0.2px;
}
.fee-price {
  flex-shrink: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
}

/* 明细底部总费用行 */
.fee-total {
  background: rgba(44, 123, 229, 0.04);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}
.fee-total .fee-name {
  font-size: 16px;
  font-weight: 700;
}
.fee-total .total-price {
  font-size: 19px;
}

/* 响应式 */
@media (max-width: 480px) {
  .content-scroll {
    padding: 12px;
  }
  .info-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  .company-name {
    font-size: 20px;
  }
  .fee-header-title {
    font-size: 15px;
  }
  .total-price {
    font-size: 18px;
  }
}
</style>
